/**
 * Template Registry — single source of truth for landing page template data.
 *
 * Imports the API's template YAML files directly via @modyfi/vite-plugin-yaml.
 * This ensures the landing page always reflects the actual template definitions
 * without duplicating data.
 */

// @ts-expect-error - YAML imports are handled by @modyfi/vite-plugin-yaml at build time
import frontendBackendYml from "../../../api/config/templates/frontend-backend.yml";
// @ts-expect-error - YAML imports are handled by @modyfi/vite-plugin-yaml at build time
import dataEngineeringYml from "../../../api/config/templates/data-engineering.yml";
// @ts-expect-error - YAML imports are handled by @modyfi/vite-plugin-yaml at build time
import analyticalYml from "../../../api/config/templates/analytical.yml";
// @ts-expect-error - YAML imports are handled by @modyfi/vite-plugin-yaml at build time
import awsYml from "../../../api/config/templates/aws.yml";
// @ts-expect-error - YAML imports are handled by @modyfi/vite-plugin-yaml at build time
import observabilityYml from "../../../api/config/templates/observability.yml";
// @ts-expect-error - YAML imports are handled by @modyfi/vite-plugin-yaml at build time
import databasesYml from "../../../api/config/templates/databases.yml";

export type ServiceType =
  | "static"
  | "server"
  | "stateful"
  | "scheduler"
  | "cronjob";

export type Complexity = "low" | "medium" | "high";

export type SystemType =
  | "frontend_backend"
  | "data_engineering"
  | "analytical"
  | "observability"
  | "database";

export interface ServiceSpec {
  name: string;
  type: ServiceType;
  port?: number;
  image?: string;
  internetFacing: boolean;
  storage?: string;
  healthCheck?: {
    path?: string;
    command?: string[];
    intervalSeconds?: number;
    timeoutSeconds?: number;
    failureThreshold?: number;
  };
  vcs?: {
    baseImage?: string;
    dockerfile?: string;
    buildCommand?: string;
    buildDir?: string;
  };
  resources: {
    cpu: string;
    memory: string;
  };
}

export interface StackConfig {
  [key: string]: string | null | undefined;
}

export interface VcsConfig {
  required: boolean;
  purpose: string;
  repoTemplate?: string;
  repoSubdir?: string;
  branches?: { dev: string; staging: string; production: string };
  localDev?: { compose: boolean };
}

export interface TemplateEntry {
  id: string;
  slug: string;
  name: string;
  description: string;
  useCase: string;
  systemType: SystemType;
  complexity: Complexity;
  language: string;
  tags: string[];
  deploymentModes: string[];
  stackConfig: StackConfig;
  vcs: VcsConfig;
  services: ServiceSpec[];
}

interface YamlTemplateFile {
  templates: TemplateEntry[];
}

const allTemplates: TemplateEntry[] = [
  ...(frontendBackendYml as YamlTemplateFile).templates,
  ...(dataEngineeringYml as YamlTemplateFile).templates,
  ...(analyticalYml as YamlTemplateFile).templates,
  ...(awsYml as YamlTemplateFile).templates,
  ...(observabilityYml as YamlTemplateFile).templates,
  ...(databasesYml as YamlTemplateFile).templates,
];

export const templates: TemplateEntry[] = allTemplates;

export function getTemplateBySlug(slug: string): TemplateEntry | undefined {
  return templates.find((t) => t.slug === slug);
}

export function getTemplatesByCategory(
  systemType: SystemType,
): TemplateEntry[] {
  return templates.filter((t) => t.systemType === systemType);
}

export interface TemplateCategory {
  id: string;
  name: string;
  description: string;
  systemType: SystemType;
  templates: TemplateEntry[];
}

export const templateCategories: TemplateCategory[] = [
  {
    id: "frontend-backend",
    name: "Web Applications",
    description:
      "Full-stack web applications with React, Next.js, Vue, Angular, and more",
    systemType: "frontend_backend",
    templates: getTemplatesByCategory("frontend_backend"),
  },
  {
    id: "data-engineering",
    name: "Data Engineering",
    description: "ELT pipelines, lakehouses, streaming, and CDC workloads",
    systemType: "data_engineering",
    templates: getTemplatesByCategory("data_engineering"),
  },
  {
    id: "analytical",
    name: "Analytics & BI",
    description:
      "OLAP databases, real-time analytics, and business intelligence dashboards",
    systemType: "analytical",
    templates: getTemplatesByCategory("analytical"),
  },
  {
    id: "observability",
    name: "Observability",
    description:
      "Metrics, logs, traces, and full-stack monitoring with Prometheus, Grafana, Loki, and Jaeger",
    systemType: "observability",
    templates: getTemplatesByCategory("observability"),
  },
  {
    id: "database",
    name: "Databases",
    description:
      "High-availability database clusters with automatic failover for PostgreSQL, Redis, MongoDB, and MySQL",
    systemType: "database",
    templates: getTemplatesByCategory("database"),
  },
];

export const totalTemplateCount = templates.length;
